import type { DatasetStructure, DatasetStructureChannel } from "@/api";

// 멀티 데이터셋 제출의 채널 매핑 후보. BE `intersect_structures` 의 사본이다. 학습이 N개
// 데이터셋을 한 배치로 섞으므로, 고를 수 있는 채널은 **모든 데이터셋에 있고 dim·media 가
// 같은 채널**뿐이다. 한 데이터셋에만 있는 카메라를 고르면 나머지 데이터셋의 샘플이 그 자리를
// 채우지 못한다.
//
// BE 는 공통 채널 목록만 반환한다. 화면은 "이 카메라가 왜 사라졌는지"를 알려야 하므로 이 함수는
// 제외 사유도 함께 반환한다.

// 채널 하나가 교집합에서 빠진 이유.
export type ChannelExclusion = {
  topic: string;
  name: string;
  // missing = 그 채널이 없는 데이터셋이 있다. *_mismatch = 채널은 있지만 값이 다르다.
  kind: "missing" | "dim_mismatch" | "media_mismatch";
  // missing 이면 그 채널이 없는 데이터셋들, mismatch 면 대표 데이터셋과 값이 다른 데이터셋들이다.
  datasetUuids: string[];
};

export type StructureIntersection = {
  // 모든 데이터셋이 공유하는 채널. 첫 데이터셋의 인스턴스를 대표로 쓴다.
  // topic·name·dim·media 는 교집합 판정으로 같다는 것이 보장된다. 하지만 **episode_count 는 그
  // 데이터셋의 값**이라 N개 전체의 커버리지가 아니다(호출부가 그 표기를 정한다).
  channels: DatasetStructureChannel[];
  // 제외된 채널. 첫 데이터셋 기준 순서 뒤에, 뒤쪽 데이터셋에만 있는 채널을 추가한다.
  excluded: ChannelExclusion[];
};

function channelKey(channel: { topic: string; name: string }): string {
  return `${channel.topic}/${channel.name}`;
}

export function intersectDatasetStructures(
  structures: readonly DatasetStructure[],
): StructureIntersection {
  // 데이터셋이 하나면 교집합은 항등이다. 단일 제출 경로도 분기 없이 같은 함수를 쓴다.
  if (structures.length <= 1) {
    return { channels: [...(structures[0]?.channels ?? [])], excluded: [] };
  }

  const byDataset = structures.map(
    (structure) =>
      new Map(structure.channels.map((channel) => [channelKey(channel), channel] as const)),
  );

  const channels: DatasetStructureChannel[] = [];
  const excluded: ChannelExclusion[] = [];
  const seen = new Set<string>();

  // 첫 데이터셋 순서를 유지한다. 화면의 채널 순서가 제출마다 바뀌지 않게 한다.
  for (const candidate of structures[0].channels) {
    seen.add(channelKey(candidate));
    const exclusion = findExclusion(candidate, structures, byDataset);
    if (exclusion === null) {
      channels.push(candidate);
    } else {
      excluded.push(exclusion);
    }
  }

  // 뒤쪽 데이터셋에만 있는 채널. 첫 데이터셋에 없으므로 모두 제외 대상이다.
  for (let index = 1; index < structures.length; index += 1) {
    for (const candidate of structures[index].channels) {
      const key = channelKey(candidate);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      excluded.push({
        topic: candidate.topic,
        name: candidate.name,
        kind: "missing",
        datasetUuids: structures
          .filter((_, other) => !byDataset[other].has(key))
          .map((structure) => structure.dataset_uuid),
      });
    }
  }

  return { channels, excluded };
}

// 부재를 먼저 확인한다. 없는 데이터셋이 있으면 값 비교는 의미가 없고, "없다"가 사용자가 조치할 수 있는 안내다.
function findExclusion(
  candidate: DatasetStructureChannel,
  structures: readonly DatasetStructure[],
  byDataset: readonly ReadonlyMap<string, DatasetStructureChannel>[],
): ChannelExclusion | null {
  const key = channelKey(candidate);
  const missing = structures
    .filter((_, index) => !byDataset[index].has(key))
    .map((structure) => structure.dataset_uuid);
  if (missing.length > 0) {
    return { topic: candidate.topic, name: candidate.name, kind: "missing", datasetUuids: missing };
  }

  // dim 을 media 보다 먼저 확인한다. 차원 불일치는 학습을 바로 실패시키는 축이다.
  const dimMismatch = mismatchedDatasets(
    candidate,
    structures,
    byDataset,
    (channel) => channel.dim,
  );
  if (dimMismatch.length > 0) {
    return {
      topic: candidate.topic,
      name: candidate.name,
      kind: "dim_mismatch",
      datasetUuids: dimMismatch,
    };
  }

  const mediaMismatch = mismatchedDatasets(
    candidate,
    structures,
    byDataset,
    (channel) => channel.media,
  );
  if (mediaMismatch.length > 0) {
    return {
      topic: candidate.topic,
      name: candidate.name,
      kind: "media_mismatch",
      datasetUuids: mediaMismatch,
    };
  }
  return null;
}

function mismatchedDatasets(
  candidate: DatasetStructureChannel,
  structures: readonly DatasetStructure[],
  byDataset: readonly ReadonlyMap<string, DatasetStructureChannel>[],
  value: (channel: DatasetStructureChannel) => unknown,
): string[] {
  const key = channelKey(candidate);
  const expected = value(candidate);
  return structures
    .filter((_, index) => {
      const channel = byDataset[index].get(key);
      return channel !== undefined && !Object.is(value(channel), expected);
    })
    .map((structure) => structure.dataset_uuid);
}
